import type React from 'react';

import Checkbox from '@mui/material/Checkbox';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';

import StandardButton from 'components/buttons/StandardButton';
import { useStyles } from 'components/dialogs/doubles/styles';
import TMXDialog from 'components/dialogs/TMXDialog';
import { useProgress } from 'hooks/tournament/useSaveTournament';

interface DoubleTeamsRemoveDialogProps {
  open?: boolean;
  removePair?: Array<any>;
  onCancel: () => void;
  onChangeCheckbox: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  onRemove: () => void;
  onExited?: () => void;
}

const DoubleTeamsRemoveDialog: React.FC<React.PropsWithChildren<DoubleTeamsRemoveDialogProps>> = ({
  open,
  removePair,
  onCancel,
  onChangeCheckbox,
  onRemove,
  onExited,
}) => {
  const { t } = useTranslation();

  const removeInProgress = useProgress('remove');
  const isOpen = removeInProgress ? true : open;
  const { classes } = useStyles();
  const paperProps = { style: { padding: 30 } };

  return (
    <TMXDialog open={isOpen} PaperProps={paperProps} TransitionProps={{ onExited }}>
      <Typography variant="h1">{t('removePlayers')}</Typography>
      <br />
      <Typography>
        {t('doubleTeamsRemoveDialog.contentOneNormalOne')}{' '}
        <span className={classes.boldSpan}>{t('doubleTeamsRemoveDialog.contentOneBold')}</span>
        {t('doubleTeamsRemoveDialog.contentOneNormalTwo')}
      </Typography>
      <br />
      <Typography>
        {t('doubleTeamsRemoveDialog.contentTwoNormalOne')}
        <span className={classes.boldSpan}>{t('doubleTeamsRemoveDialog.contentTwoBold')}</span>
        {t('doubleTeamsRemoveDialog.contentTwoNormalTwo')}
      </Typography>
      <br />
      <Typography variant="h3">{t('withdraw')}:</Typography>
      <Grid container direction="row" spacing={2}>
        <Grid item>
          <Checkbox
            checked={removePair ? removePair[0].checked : false}
            color="primary"
            disabled={removePair?.[0].pairedElsewhere}
            value={removePair ? removePair[0].individualParticipantId : ''}
            onChange={onChangeCheckbox}
          />
        </Grid>
        <Grid item className={classes.gridItem}>
          <Typography className={cx({ [classes.disabledWithdrawText]: removePair?.[0].pairedElsewhere })}>
            {removePair ? removePair[0].name : ''}
          </Typography>
          {removePair?.[0].pairedElsewhere && <CannotWithdrawMessage />}
        </Grid>
      </Grid>
      <Grid container direction="row" spacing={2}>
        <Grid item>
          <Checkbox
            checked={removePair ? removePair[1].checked : false}
            color="primary"
            disabled={removePair?.[1].pairedElsewhere}
            value={removePair ? removePair[1].individualParticipantId : ''}
            onChange={onChangeCheckbox}
          />
        </Grid>
        <Grid item className={classes.gridItem}>
          <Typography className={cx({ [classes.disabledWithdrawText]: removePair?.[1].pairedElsewhere })}>
            {removePair ? removePair[1].name : ''}
          </Typography>
          {removePair?.[1].pairedElsewhere && <CannotWithdrawMessage />}
        </Grid>
      </Grid>
      <Grid container direction="row" justifyContent="flex-end" spacing={2}>
        <Grid item>
          <StandardButton onClick={onCancel}>{t('ccl')}</StandardButton>
        </Grid>
        <Grid item>
          <StandardButton loading={removeInProgress} variant="warning" onClick={onRemove}>
            {t('remove')}
          </StandardButton>
        </Grid>
      </Grid>
    </TMXDialog>
  );
};

const CannotWithdrawMessage: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  return <Typography className={classes.cannotWithdrawMessage}>{t('playerPairedElsewhere')}</Typography>;
};

export default DoubleTeamsRemoveDialog;
